type JwtObject = Record<string, unknown>;

export interface iJwtAuthContext {
  userId: string | null;
  customer: string;
  customerId: number | null;
  tenant: string | null;
  tenantId: number | null;
  tenantType: string | null;
}

const DEFAULT_AUTH_CONTEXT: iJwtAuthContext = {
  userId: null,
  customer: '',
  customerId: null,
  tenant: null,
  tenantId: null,
  tenantType: null,
};

function isJwtObject(value: unknown): value is JwtObject {
  return typeof value === 'object' && value != null && !Array.isArray(value);
}

function decodeJwtBody(token: string): JwtObject | null {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      window
        .atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );

    const body = JSON.parse(jsonPayload);

    return isJwtObject(body) ? body : null;
  } catch {
    return null;
  }
}

function getUserPayload(body: JwtObject | null): JwtObject | null {
  if (!body) {
    return null;
  }

  if (isJwtObject(body.user)) {
    return body.user;
  }

  if (typeof body.aud === 'string') {
    const scopedUser = body[`${body.aud}/app`];

    if (isJwtObject(scopedUser)) {
      return scopedUser;
    }
  }

  return null;
}

function getString(value: unknown): string | null {
  return typeof value === 'string' ? value : null;
}

function getId(value: unknown): number | null {
  if (typeof value === 'string' || typeof value === 'number') {
    const parsedValue = Number(value);
    return Number.isFinite(parsedValue) ? parsedValue : null;
  }

  return null;
}

export function getAuthContextFromJwt(token: string): iJwtAuthContext {
  const user = getUserPayload(decodeJwtBody(token));

  if (!user) {
    return { ...DEFAULT_AUTH_CONTEXT };
  }

  return {
    userId: getString(user.user_id),
    customer: getString(user.customer) ?? '',
    customerId: getId(user.customer_id),
    tenant: getString(user.tenant),
    tenantId: getId(user.tenant_id),
    tenantType: getString(user.tenant_type),
  };
}

export const getJwtContextFromToken = (token: string): iJwtAuthContext => getAuthContextFromJwt(token);

export function getCustomerFromJwt(token: string) {
  return getAuthContextFromJwt(token).customer;
}

export function getUserIdFromJwt(token: string): string | null {
  return getAuthContextFromJwt(token).userId;
}
